import { useDeferredValue, useMemo, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Button, cn, EmptyState, ErrorState, LoadingState, SectionHeader } from '@pixlide/ui';
import { useTemplatesAdmin, type TemplateAnimationInspection } from '@/api/queries/templates';
import { CreateTemplateForm } from '@/features/templates/CreateTemplateForm';
import { InspectionSummary } from '@/features/templates/InspectionSummary';
import { TemplateCard } from '@/features/templates/TemplateCard';
import { statusLabels, type TemplateStatusValue } from '@/features/templates/template-ui';

type StatusFilter = 'all' | TemplateStatusValue;

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'Tous' },
  { value: 0, label: 'Brouillons' },
  { value: 1, label: 'Publiés' },
  { value: 2, label: 'Archivés' },
];

/**
 * Bibliothèque de templates vidéo Lottie : création (animation After Effects/Bodymovin +
 * vignette), schéma de champs personnalisables, aperçu par le moteur de production,
 * publication. Les filtres et la recherche s'appliquent côté navigateur : la liste admin est
 * déjà chargée en entier, et une bibliothèque se compte en dizaines de templates.
 */
export function TemplatesPage() {
  const templates = useTemplatesAdmin();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [lastInspection, setLastInspection] = useState<TemplateAnimationInspection | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');

  // Différée : filtrer des cartes riches à chaque frappe ne doit pas ralentir la saisie.
  const deferredSearch = useDeferredValue(search);

  const counts = useMemo(() => {
    const result: Record<StatusFilter, number> = { all: 0, 0: 0, 1: 0, 2: 0 };

    for (const template of templates.data ?? []) {
      result.all += 1;
      result[template.status] += 1;
    }

    return result;
  }, [templates.data]);

  const visible = useMemo(() => {
    const needle = deferredSearch.trim().toLocaleLowerCase('fr');

    return (templates.data ?? []).filter(
      (template) =>
        (statusFilter === 'all' || template.status === statusFilter)
        && (needle === ''
          || template.name.toLocaleLowerCase('fr').includes(needle)
          || template.category.toLocaleLowerCase('fr').includes(needle)),
    );
  }, [templates.data, statusFilter, deferredSearch]);

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      <SectionHeader
        title="Templates vidéo"
        description="Animations Lottie (export After Effects/Bodymovin) personnalisables par les clients."
        action={
          <Button
            variant={showCreateForm ? 'ghost' : 'primary'}
            size="sm"
            onClick={() => setShowCreateForm((current) => !current)}
          >
            {showCreateForm ? (
              <>
                <X aria-hidden className="size-4" />
                Annuler
              </>
            ) : (
              <>
                <Plus aria-hidden className="size-4" />
                Nouveau template
              </>
            )}
          </Button>
        }
      />

      {showCreateForm ? (
        <CreateTemplateForm
          onCreated={(inspection) => {
            setShowCreateForm(false);
            setLastInspection(inspection);
            setStatusFilter('all');
          }}
        />
      ) : null}

      {lastInspection ? (
        <InspectionSummary inspection={lastInspection} onClose={() => setLastInspection(null)} />
      ) : null}

      {templates.isPending ? <LoadingState label="Chargement des templates…" /> : null}

      {templates.isError ? (
        <ErrorState message="Impossible de charger les templates." onRetry={() => void templates.refetch()} />
      ) : null}

      {templates.data && templates.data.length > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div role="group" aria-label="Filtrer par statut" className="flex flex-wrap gap-1.5">
            {STATUS_FILTERS.map((filter) => (
              <button
                key={filter.value}
                type="button"
                aria-pressed={statusFilter === filter.value}
                onClick={() => setStatusFilter(filter.value)}
                className={cn(
                  'rounded-full px-3 py-1 text-sm',
                  statusFilter === filter.value
                    ? 'bg-ink-900 text-white'
                    : 'bg-white text-ink-500 ring-1 ring-ink-100 hover:text-ink-900',
                )}
              >
                {filter.label} <span className="opacity-70">{counts[filter.value]}</span>
              </button>
            ))}
          </div>

          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Rechercher un nom ou une catégorie"
            aria-label="Rechercher un template"
            className="h-9 w-full rounded-xl bg-white px-3.5 text-sm ring-1 ring-ink-100 focus:outline-none focus:ring-2 focus:ring-ink-900 sm:w-72"
          />
        </div>
      ) : null}

      {templates.data && templates.data.length === 0 ? (
        <EmptyState
          title="Aucun template"
          description="Créez un premier template à partir d'un export Bodymovin : ses calques slot-… deviendront les champs proposés aux clients."
        />
      ) : null}

      {templates.data && templates.data.length > 0 && visible.length === 0 ? (
        <p className="text-sm text-ink-500">
          Aucun template {statusFilter === 'all' ? '' : `« ${statusLabels[statusFilter].toLowerCase()} » `}ne
          correspond à cette recherche.
        </p>
      ) : null}

      <div className="grid gap-5 md:grid-cols-2">
        {visible.map((template) => (
          <TemplateCard key={template.id} template={template} />
        ))}
      </div>
    </div>
  );
}
